import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_SERVER_URL } from 'js/constants/api';
import { getAccessToken } from 'js/reducers/session/selectors';
import {
  getDailyMetricsQueryDefinition,
  getFlowDailyMetricsQueryDefinition,
  parseDailyMetricsResponse,
  type DailyMetrics,
  type DailyMetricsParams,
  type FlowDailyMetricsArg,
  type FlowDailyMetricsParams,
} from './metrics.endpoints';

/**
 * Shape read by `getAccessToken` (see js/reducers/session/selectors.js).
 * We do not use `ReturnType<typeof rootReducer>` here: js/reducers imports this API slice,
 * so importing the combined reducer would create a circular module dependency.
 */
type ReduxStateForAccessToken = {
  session: Record<string, unknown>;
  entities: {
    users?: Record<string, unknown>;
    orgs?: Record<string, unknown>;
  };
};

export const metricsApi = createApi({
  reducerPath: 'metricsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_SERVER_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getAccessToken(getState() as ReduxStateForAccessToken);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getDailyMetrics: builder.query<DailyMetrics, DailyMetricsParams>({
      query: getDailyMetricsQueryDefinition,
      transformResponse: parseDailyMetricsResponse,
    }),
    getFlowDailyMetrics: builder.query<DailyMetrics, FlowDailyMetricsArg>({
      query: getFlowDailyMetricsQueryDefinition,
      transformResponse: parseDailyMetricsResponse,
    }),
  }),
});

/**
 * Note For Dev:
 * - Export here what you need to consume, treat these as the source of truth for the metrics API.
 * - Do not import `metricsApi` in feature code, use the exports instead.
 */

export const useDailyMetrics = (params: DailyMetricsParams, options?: { skip?: boolean }) => {
  const result = metricsApi.useGetDailyMetricsQuery(params, options);
  return result;
};

export const useFlowDailyMetrics = (
  originNodeId: number | string,
  params: FlowDailyMetricsParams,
  options?: { skip?: boolean },
) => {
  return metricsApi.useGetFlowDailyMetricsQuery({ originNodeId, ...params }, options);
};
